import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MapRef } from 'react-map-gl'

export enum MapboxDrawMode {
  DRAW_POLYGON = 'draw_polygon',
  SIMPLE_SELECT = 'simple_select',
}

/**
 * A hook that handles the MapboxDraw actions to draw a single polygon on a mapbox map.
 * This hook returns things to help get handle polygon draw-actions, like:
 * - clear all drawings (clearDrawings)
 * - get the draw mode (drawMode)
 * - change the draw mode (setDrawMode)
 * - get the drawn geometry (getDrawnGeometry)
 * - put a geometry on the map (putGeometryOnMap)
 */
export function useMapboxDraw(
  mapRef: MapRef | null | undefined,
  onDrawComplete: () => void
) {
  const [drawMode, setDrawMode] = useState<MapboxDrawMode>(
    MapboxDrawMode.SIMPLE_SELECT
  )

  const draw = useMemo(
    () =>
      new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          point: false,
          line_string: false,
          polygon: false,
          trash: false,
          combine_features: false,
          uncombine_features: false,
        },
      }),
    []
  )

  useEffect(() => {
    if (!mapRef) {
      return
    }

    mapRef.addControl(draw)

    const handleDrawCreate = () => {
      // Ensure only one polygon exists
      const features = draw.getAll()
      if (features.features.length > 1) {
        const mostRecentFeature =
          features.features[features.features.length - 1]
        draw.deleteAll()
        draw.add(mostRecentFeature)
      }

      onDrawComplete()
    }

    const handleDrawUpdate = () => {
      onDrawComplete()
    }

    mapRef.on('draw.create', handleDrawCreate)
    mapRef.on('draw.update', handleDrawUpdate)

    return () => {
      mapRef.off('draw.create', handleDrawCreate)
      mapRef.off('draw.update', handleDrawUpdate)
    }
  }, [mapRef, draw, onDrawComplete])

  useEffect(() => {
    if (!mapRef) {
      return
    }

    draw.changeMode(drawMode as string)
  }, [drawMode, draw, mapRef])

  const clearDrawings = useCallback(() => {
    if (!mapRef) {
      return
    }
    draw.deleteAll()
  }, [mapRef, draw])

  const getDrawnGeometry = useCallback((): GeoJSON.Geometry | null => {
    if (!mapRef) {
      return null
    }
    const features = draw.getAll()
    return (features.features.at(0)?.geometry ??
      null) as GeoJSON.Geometry | null
  }, [mapRef, draw])

  const putGeometryOnMap = useCallback(
    (geometry: GeoJSON.Geometry) => {
      if (!mapRef) {
        return
      }
      draw.add({
        type: 'Feature',
        geometry: geometry as GeoJSON.Geometry,
        properties: {},
      })
    },
    [mapRef, draw]
  )

  return {
    clearDrawings,
    setDrawMode,
    drawMode,
    getDrawnGeometry,
    putGeometryOnMap,
  }
}
